import type { CSSProperties } from 'react';
import type { LearnMoreModalData } from '../DiagnosticLearnMoreModal';
import { getBuyPrioritizedModalData } from '../modalDataFromBuy';

export interface CollapsibleItem {
  id: string;
  title: string;
  description: string;
  learnMoreText?: string;
  modalData?: LearnMoreModalData;
  panelImageSrc: string;
}

export type CollapsibleIdleHudCorner =
  | 'topLeft'
  | 'topRight'
  | 'bottomLeft'
  | 'bottomRight';

export interface CollapsibleSectionIdleHud {
  line1Key: string;
  line2Key: string;
  labelCorner?: CollapsibleIdleHudCorner;
}

export interface CollapsibleSectionData {
  heading: string;
  subtitle: string;
  imageSrc?: string;
  videoSrc?: string;
  bgBlendMode?: CSSProperties['mixBlendMode'];
  idleHud?: CollapsibleSectionIdleHud;
  items: CollapsibleItem[];
}

function recoveryPanelAsset(filename: string): string {
  return `web_v2/performance-lab/landing-page/accordions/recovery-v2/${filename}/w_1080`;
}

function diagnosticPanelAsset(filename: string): string {
  return `web_v2/performance-lab/landing-page/accordions/diagnostics-v2/${filename}/w_1080`;
}

const withBuyModalData = (items: CollapsibleItem[]): CollapsibleItem[] =>
  items.map((item) => ({
    ...item,
    modalData: getBuyPrioritizedModalData(item.id),
  }));

export const DIAGNOSTICS_DATA: CollapsibleSectionData = {
  heading: 'Deep Diagnostics',
  subtitle:
    'Advanced diagnostics measure up to 3500 biological markers across metabolism, cardiovascular fitness, body composition, biomechanics, sleep, and genetic predispositions.',
  videoSrc:
    'web_v2/performance-lab/landing-page/accordions/diagnostics/diagnostic-banner.mp4',
  bgBlendMode: 'darken',
  idleHud: {
    line1Key: 'accordion.diagnostics.idle.line1',
    line2Key: 'accordion.diagnostics.idle.line2',
    labelCorner: 'topLeft',
  },
  items: withBuyModalData([
    {
      id: 'blood-vision',
      title: 'Blood Vision',
      description:
        'Reveals a comprehensive set of biomarkers across metabolic, hormonal, nutritional, and organ health.',
      learnMoreText: 'Learn more',
      panelImageSrc: diagnosticPanelAsset('blood-vision-1.png'),
    },
    {
      id: 'body-composition-analysis',
      title: 'Body Composition Analysis',
      description:
        'The gold standard for analyzing body composition across fat mass, lean muscle, and bone density to reveal how your body is structurally built and where it’s aging.',
      learnMoreText: 'Learn more',
      panelImageSrc: diagnosticPanelAsset('body-composition-analysis-1.png'),
    },
    {
      id: 'bone-density',
      title: 'Bone Density',
      description:
        'Measures bone mineral density and regional distribution to flag early structural weakness and guide long-term skeletal resilience strategies.',
      learnMoreText: 'Learn more',
      panelImageSrc: diagnosticPanelAsset('bone-density-1.png'),
    },
    {
      id: 'electrocardiogram',
      title: 'Electrocardiogram',
      description:
        "An Electrocardiogram (ECG) records your heart's electrical activity to identify rhythm irregularities, conduction issues, and early markers of cardiovascular risk at rest.",
      learnMoreText: 'Learn more',
      panelImageSrc: diagnosticPanelAsset('electrocardiogram.png'),
    },
    {
      id: 'vitals',
      title: 'Vitals',
      description:
        'Captures core resting vitals to establish a physiological baseline for cardiovascular strain, recovery readiness, and ongoing health monitoring.',
      learnMoreText: 'Learn more',
      panelImageSrc: diagnosticPanelAsset('vitals.png'),
    },
    {
      id: 'vision',
      title: 'Vision',
      description:
        'Captures the back of your eye through images to assess vascular and neural tissue – an early detection window not just for vision problems, but for systemic conditions like diabetes and hypertension.',
      learnMoreText: 'Learn more',
      panelImageSrc: diagnosticPanelAsset('vision.png'),
    },
    {
      id: 'hearing',
      title: 'Hearing',
      description:
        'Tests your hearing range and frequency sensitivity to catch early-stage hearing loss – a commonly overlooked risk factor for cognitive decline and reduced quality of life.',
      learnMoreText: 'Learn more',
      panelImageSrc: diagnosticPanelAsset('hearing.png'),
    },
    {
      id: 'oral-health',
      title: 'Oral Health',
      description:
        'Screens for decay, gum disease, and oral structural issues – with direct links to cardiovascular health, inflammation, and overall systemic well-being.',
      learnMoreText: 'Learn more',
      panelImageSrc: diagnosticPanelAsset('oral_health.png'),
    },
    {
      id: 'cognition',
      title: 'Cognition',
      description:
        'A cognitive assessment benchmarks your attention, processing speed, inhibitory control and working memory, to establish a mental baseline and track cognitive changes over time.',
      learnMoreText: 'Learn more',
      panelImageSrc: diagnosticPanelAsset('cognition.png'),
    },
    {
      id: 'lung-health',
      title: 'Pulmonary Function Test',
      description:
        'A Pulmonary Function Test (PFT) uses spirometry to measure airflow and capacity, helping detect early respiratory changes before symptoms arise.',
      learnMoreText: 'Learn more',
      panelImageSrc: diagnosticPanelAsset('lung_health.png'),
    },
    {
      id: 'vo2-max',
      title: 'VO₂ Max',
      description:
        'Measures how efficiently your body uses oxygen during exertion – one of the most important indicators of cardiovascular fitness and longevity.',
      learnMoreText: 'Learn more',
      panelImageSrc: diagnosticPanelAsset('vo2_max.png'),
    },
    {
      id: 'musculoskeletal-assessment',
      title: 'Musculoskeletal Assessment',
      description:
        'Evaluates your upper body and lower body strength objectively making it easier to personalize training and track progress.',
      learnMoreText: 'Learn more',
      panelImageSrc: diagnosticPanelAsset('musculoskeletal_assessment.png'),
    },
    {
      id: 'genetics',
      title: 'Genetics',
      description:
        'Genetic testing analyzes your DNA for predispositions across cardiovascular, metabolic, and cancer risk categories – turning results into actionable prevention strategies.',
      learnMoreText: 'Learn more',
      panelImageSrc: diagnosticPanelAsset('genetics.png'),
    },
    {
      id: 'nutrition',
      title: 'Nutrition',
      description:
        'Evaluates eating patterns, nutrient intake, and metabolic needs to build a nutrition strategy tuned to the biology.',
      learnMoreText: 'Learn more',
      panelImageSrc: diagnosticPanelAsset('nutrition.png'),
    },
    {
      id: 'gut-microbiome',
      title: 'Gut Microbiome',
      description:
        'Maps the composition of your gut microbial composition to reveal how your microbiome is influencing digestion, immunity, inflammation, and even mood.',
      learnMoreText: 'Learn more',
      panelImageSrc: diagnosticPanelAsset('gut_microbiome.png'),
    },
    {
      id: 'sleep-study-polysomnography',
      title: 'Sleep Study Polysomnography',
      description:
        'Monitors breathing patterns, brain activity, and sleep cycles to uncover hidden sleep disruptions.',
      learnMoreText: 'Learn more',
      panelImageSrc: diagnosticPanelAsset('sleep_study_polysomnography.png'),
    },
  ]),
};

export const RECOVERY_DATA: CollapsibleSectionData = {
  heading: 'Recover Smarter',
  subtitle:
    'A curated suite of evidence-backed recovery protocols designed to accelerate cellular repair, reduce inflammation, and optimize metabolic recovery.',
  videoSrc:
    'web_v2/performance-lab/landing-page/accordions/recovery/recovery-banner.mp4',
  idleHud: {
    line1Key: 'accordion.recovery.idle.line1',
    line2Key: 'accordion.recovery.idle.line2',
    labelCorner: 'topLeft',
  },
  items: withBuyModalData([
    {
      id: 'hyperbaric-oxygen-therapy-hbot',
      title: 'Hyperbaric Oxygen Therapy (HBOT)',
      description:
        'Delivers 100% pure oxygen in a pressurized chamber to support cellular repair, tissue recovery, and improved oxygen delivery.',
      learnMoreText: 'Learn more about HBOT',
      panelImageSrc: recoveryPanelAsset('hyperbaric_oxygen_therapy_hbot.png'),
    },
    {
      id: 'infrared-sauna',
      title: 'Infrared Sauna',
      description:
        'Uses far, middle, and near infrared light to promote cellular repair, cardiovascular conditioning, deep muscle recovery, and detoxification from the inside out.',
      learnMoreText: 'Learn more about Infrared Sauna',
      panelImageSrc: recoveryPanelAsset('infrared-sauna-new.png'),
    },
    {
      id: 'cold-plunge',
      title: 'Cold Plunge',
      description:
        'Applies controlled cold immersion to reduce inflammation, improve circulation, and support muscular recovery.',
      learnMoreText: 'Learn more about Cold Plunge',
      panelImageSrc: recoveryPanelAsset('cold_plunge.png'),
    },
    {
      id: 'cryotherapy',
      title: 'Cryotherapy',
      description:
        'Exposes the body to extreme cold for 1-3 minutes to trigger a systemic recovery response that helps reduce inflammation and improve post-training readiness.',
      learnMoreText: 'Learn more about Cryotherapy',
      panelImageSrc: recoveryPanelAsset('cryotherapy-new.png'),
    },
    {
      id: 'decompression',
      title: 'Decompression',
      description:
        'Uses controlled inversion-table protocols to reduce spinal compression, relieve back tension, and support structural recovery after sustained physical load.',
      learnMoreText: 'Learn more about Decompression',
      panelImageSrc: recoveryPanelAsset('decompression.png'),
    },
    {
      id: 'powerplate',
      title: 'Powerplate',
      description:
        'Uses harmonic vibration to trigger neuromuscular activation, improve circulation, and prime your body for peak performance or accelerate recovery post workout sessions.',
      learnMoreText: 'Learn more about Powerplate',
      panelImageSrc: recoveryPanelAsset('powerplate.png'),
    },
    {
      id: 'massage-guns',
      title: 'Massage Guns',
      description:
        'Applies targeted percussive therapy to reduce muscular tightness, improve mobility, and accelerate soft-tissue recovery between sessions.',
      learnMoreText: 'Learn more about Massage Guns',
      panelImageSrc: recoveryPanelAsset('massage_guns.png'),
    },
    {
      id: 'compression',
      title: 'Compression',
      description:
        'Pneumatic leg compression & Thermacube drive targeted blood flow, flush metabolic waste, and reduce inflammation through mechanical compression and precision temperature therapy.',
      learnMoreText: 'Learn more about Compression',
      panelImageSrc: recoveryPanelAsset('compression-1.png'),
    },
    {
      id: 'localized-hot-cold-therapy',
      title: 'Localized Hot & Cold Therapy',
      description:
        'Alternates focused heat and cold exposure to specific regions to support circulation, reduce local inflammation, and improve tissue recovery.',
      learnMoreText: 'Learn more about Localized Hot & Cold Therapy',
      panelImageSrc: recoveryPanelAsset('localised-hot-cold-therapy-1.png'),
    },
    {
      id: 'iv-therapy',
      title: 'IV Therapy',
      description:
        "Replenishes essential vitamins, minerals, and fluids through intravenous infusion bypassing digestion for immediate effect – whether you're fighting off illness, accelerating recovery, overcoming a hangover, or restoring metabolic balance.",
      learnMoreText: 'Learn more about IV Therapy',
      panelImageSrc: recoveryPanelAsset('iv_therapy.png'),
    },
    {
      id: 'im-injections',
      title: 'IM Injections',
      description:
        'Targeted intramuscular delivery of vitamins, minerals, and recovery compounds that are faster acting and more precisely dosed than oral supplementation.',
      learnMoreText: 'Learn more about IM Injections',
      panelImageSrc: recoveryPanelAsset('im_injections.png'),
    },
    {
      id: 'sports-massage',
      title: 'Sports Massage',
      description:
        'Relieves muscle tension and improves mobility to support recovery and physical performance.',
      learnMoreText: 'Learn more about Sports Massage',
      panelImageSrc: recoveryPanelAsset('sports_massage.png'),
    },
  ]),
};
